import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase/firebase';
import LibrarySection from './LibrarySection';
import Notification from './Notification';
import { sendPasswordResetEmail } from 'firebase/auth';

interface AccountProps {
  onNavigateHome?: () => void;
}

const Account: React.FC<AccountProps> = ({ onNavigateHome }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<'profile' | 'library' | 'settings'>('library');
  const [loading, setLoading] = useState(false);

  const theme = {
    primary: '#FF6B35',
    secondary: '#9B59B6',
    accent: '#F39C12',
    success: '#2ECC71',
    pink: '#E91E63',
    blue: '#3498DB'
  };

  const [notification, setNotification] = useState<{
    show: boolean;
    type: 'save' | 'publish' | 'unpublish' | 'republish' | 'tags' | 'error' | 'password-reset';
    errorMessage?: string;
  }>({ show: false, type: 'save' });

  const handleSignOut = async () => {
    try {
      setLoading(true);
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!currentUser?.email) {
      setNotification({
        show: true,
        type: 'error',
        errorMessage: 'No email found for this account!'
      });
      return;
    }
    
    const confirmed = window.confirm(
      `⚠️ Send password reset email to ${currentUser.email}?\n\n` +
      `A parent or guardian should check this email and click the reset link.`
    );
    
    if (!confirmed) return;
    
    try {
      await sendPasswordResetEmail(auth, currentUser.email);
      setNotification({
        show: true,
        type: 'password-reset'
      });
    } catch (error: any) {
      console.error('Error sending reset email:', error);
      setNotification({
        show: true,
        type: 'error',
        errorMessage: error.code === 'auth/too-many-requests' 
          ? 'Too many requests. Please wait a few minutes and try again.'
          : 'Failed to send reset email. Please try again.'
      });
    }
  };


  if (!currentUser) {
    return (
      <div style={{
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: `linear-gradient(135deg, ${theme.secondary} 0%, ${theme.primary} 50%, ${theme.accent} 100%)`,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '60px',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          border: `4px solid ${theme.primary}`,
          maxWidth: '500px'
        }}>
          <div style={{ fontSize: '72px', marginBottom: '20px' }}>🔒</div>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '800',
            margin: '0 0 15px 0',
            color: theme.primary
          }}>
            Account Required
          </h2>
          <p style={{
            fontSize: '1.1rem',
            color: '#666',
            margin: '0 0 30px 0',
            lineHeight: '1.5'
          }}>
            Please sign in to access your account and story library.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
            <button
              onClick={() => navigate('/')}
              style={{
                background: '#f8f9fa',
                color: '#666',
                border: '2px solid #e9ecef',
                padding: '12px 24px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '700'
              }}
            >
              Back to Home
            </button>
            <button
              onClick={() => navigate('/login')}
              style={{
                background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '700'
              }}
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: `linear-gradient(135deg, ${theme.secondary} 0%, ${theme.primary} 50%, ${theme.accent} 100%)`,
      minHeight: '100vh',
      padding: '20px'
    }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '30px',
        marginBottom: '30px',
        boxShadow: '0 15px 40px rgba(0,0,0,0.15)',
        border: `4px solid ${theme.primary}`
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{
              width: '60px',
              height: '60px',
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '24px',
              fontWeight: '900'
            }}>
              {currentUser.displayName ? currentUser.displayName.charAt(0).toUpperCase() : '👤'}
            </div>
            
            <div>
              <h1 style={{
                fontSize: '2rem',
                fontWeight: '900',
                margin: 0,
                background: `linear-gradient(45deg, ${theme.primary}, ${theme.secondary})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {currentUser.displayName || 'My Account'}
              </h1>
              <p style={{
                fontSize: '1rem',
                color: '#666',
                fontWeight: '600',
                margin: '5px 0 0 0'
              }}>
                {currentUser.email}
              </p>
            </div>
          </div>
          
          <button
            onClick={handleSignOut}
            disabled={loading}
            style={{
              background: loading ? '#f8f9fa' : 'transparent',
              color: loading ? '#ccc' : '#666',
              border: '2px solid #e9ecef',
              padding: '12px 20px',
              borderRadius: '12px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '700'
            }}
          >
            {loading ? 'Signing out...' : 'Sign Out'}
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{
        background: 'white',
        borderRadius: '15px',
        padding: '20px',
        marginBottom: '30px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
        border: `3px solid ${theme.secondary}`
      }}>
        <div style={{
          display: 'flex',
          gap: '15px',
          flexWrap: 'wrap'
        }}>
          {[
            { id: 'library', label: 'My Stories', icon: '📚', description: 'View and manage your stories' },
            { id: 'profile', label: 'Profile', icon: '👤', description: 'Update your profile information' },
            { id: 'settings', label: 'Settings', icon: '⚙️', description: 'Account preferences and settings' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id as any)}
              style={{
                flex: 1,
                minWidth: '200px',
                padding: '20px',
                borderRadius: '12px',
                border: `3px solid ${activeSection === tab.id ? theme.primary : '#e9ecef'}`,
                background: activeSection === tab.id ? `${theme.primary}10` : 'white',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.3s ease'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '8px'
              }}>
                <span style={{ fontSize: '24px' }}>{tab.icon}</span>
                <span style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: activeSection === tab.id ? theme.primary : '#333'
                }}>
                  {tab.label}
                </span>
              </div>
              <div style={{
                fontSize: '12px',
                color: '#666',
                lineHeight: '1.4'
              }}>
                {tab.description}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Content Sections */}
      {activeSection === 'library' && (
        <LibrarySection currentUser={currentUser} />
      )}

      {activeSection === 'profile' && (
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '40px',
          boxShadow: '0 15px 40px rgba(0,0,0,0.15)',
          border: `4px solid ${theme.blue}`
        }}>
          <h2 style={{
            fontSize: '1.8rem',
            fontWeight: '800',
            margin: '0 0 30px 0',
            color: theme.blue
          }}>
            👤 Profile Information
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#666',
                marginBottom: '8px'
              }}>
                Display Name
              </label>
              <input
                type="text"
                value={currentUser.displayName || ''}
                placeholder="Enter your display name"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e9ecef',
                  borderRadius: '10px',
                  fontSize: '14px',
                  background: '#f8f9fa',
                  color: '#999'
                }}
                disabled
              />
              <div style={{
                fontSize: '12px',
                color: '#999',
                marginTop: '5px'
              }}>
                Profile editing will be available soon
              </div>
            </div>
            
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#666',
                marginBottom: '8px'
              }}>
                Email Address
              </label>
              <input
                type="email"
                value={currentUser.email || ''}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e9ecef',
                  borderRadius: '10px',
                  fontSize: '14px',
                  background: '#f8f9fa',
                  color: '#999'
                }}
                disabled
              />
              <div style={{
                fontSize: '12px',
                color: '#999',
                marginTop: '5px'
              }}>
                Email verified: {currentUser.emailVerified ? 'Yes' : 'No'}
              </div>
            </div>
          </div>

          <div style={{
            marginTop: '40px',
            padding: '20px',
            background: `${theme.blue}10`,
            border: `2px solid ${theme.blue}30`,
            borderRadius: '12px'
          }}>
            <h3 style={{
              fontSize: '1.2rem',
              fontWeight: '700',
              margin: '0 0 15px 0',
              color: theme.blue
            }}>
              Account Statistics
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '20px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: '900',
                  color: theme.primary
                }}>
                  0
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  fontWeight: '600'
                }}>
                  Stories Created
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: '900',
                  color: theme.success
                }}>
                  0
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  fontWeight: '600'
                }}>
                  Stories Shared
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: '900',
                  color: theme.accent
                }}>
                  {new Date(currentUser.metadata.creationTime!).toLocaleDateString()}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  fontWeight: '600'
                }}>
                  Member Since
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeSection === 'settings' && (
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '40px',
          boxShadow: '0 15px 40px rgba(0,0,0,0.15)',
          border: `4px solid ${theme.accent}`
        }}>
          <h2 style={{
            fontSize: '1.8rem',
            fontWeight: '800',
            margin: '0 0 30px 0',
            color: theme.accent
          }}>
            ⚙️ Account Settings
          </h2>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '25px'
          }}>
            <div style={{
              padding: '20px',
              border: '2px solid #e9ecef',
              borderRadius: '12px'
            }}>
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: '700',
                margin: '0 0 15px 0',
                color: '#333'
              }}>
                Privacy Settings
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '15px'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}>
                  <input type="checkbox" defaultChecked />
                  <span>Allow my stories to be featured in the community gallery</span>
                </label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}>
                  <input type="checkbox" defaultChecked />
                  <span>Send me email notifications about new features</span>
                </label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}>
                  <input type="checkbox" />
                  <span>Allow other users to see my profile</span>
                </label>
              </div>
            </div>

            <div style={{
              padding: '20px',
              border: '2px solid #e9ecef',
              borderRadius: '12px'
            }}>
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: '700',
                margin: '0 0 15px 0',
                color: '#333'
              }}>
                Account Actions
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '15px'
              }}>
                <button style={{
                  background: theme.blue,
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '700',
                  textAlign: 'left'
                }}>
                  📧 Change Email Address
                </button>
                <button 
                onClick={handlePasswordReset} 
                style={{
                  background: theme.accent,
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '700',
                  textAlign: 'left'
                }}>
                  🔒 Change Password
                </button>
                <button style={{
                  background: '#ffebee',
                  color: '#d32f2f',
                  border: '2px solid #ffcdd2',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '700',
                  textAlign: 'left'
                }}>
                  🗑️ Delete Account
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
 <Notification
      isOpen={notification.show}
      onClose={() => setNotification({ ...notification, show: false })}
      type={notification.type}
      errorMessage={notification.errorMessage}
    />
  </div>  
  );
};

export default Account;